<script lang="ts">
	/**
	 * Reusable selectable card with optional image and optional process badge.
	 * Supports default, hover, and selected visual states (primary border, light background).
	 * Use for choice cards, option lists, or any single-select card UI.
	 */
	import Badge from '../atoms/Badge.svelte'

	/** Optional process/news badge. When set, shows Badge and runs process_news_badge (visibility, expiry, etc.). */
	export interface ProcessBadgeConfig {
		text: string
		newsId: string
		type: string
		color?: string
		textColor?: string
	}

	export let title: string
	export let description: string
	export let onHoverToolTip: string | undefined = undefined
	/** Optional process badge. When set, shows Badge below the image with news/process tracking. */
	export let processBadge: ProcessBadgeConfig | undefined = undefined
	/** Image URL or path for the card illustration. */
	export let imageSrc: string | undefined = undefined
	export let imageAlt: string = ''
	/** Whether this card is currently selected. */
	export let selected: boolean = false
	/** Callback when the card is clicked. */
	export let onSelect: () => void = () => {}

	const DEFAULT_BADGE_COLOR = '#D6FAE2'
	const DEFAULT_BADGE_TEXT_COLOR = '#58C079'
	// TODO: Update colors when map is merged (includes new color system)

	let hovered = false
	const isHighlighted = () => selected || hovered

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			onSelect()
		}
	}
</script>

<div
	role="button"
	title={onHoverToolTip}
	tabindex="0"
	class="selectable-card"
	class:selectable-card--highlighted={isHighlighted()}
	on:click={onSelect}
	on:keydown={handleKeydown}
	on:mouseenter={() => (hovered = true)}
	on:mouseleave={() => (hovered = false)}
>
	<h3 class="selectable-card__title">{title}</h3>
	{#if imageSrc}
		<div class="selectable-card__image-wrapper">
			<img class="selectable-card__image" src={imageSrc} alt={imageAlt} />
		</div>
	{:else}
		<div class="selectable-card__image-placeholder" aria-hidden="true"></div>
	{/if}
	{#if processBadge}
		<div class="selectable-card__badge-wrapper">
			<Badge
				text={processBadge.text}
				backgroundColor={processBadge.color ?? DEFAULT_BADGE_COLOR}
				color={processBadge.textColor ?? DEFAULT_BADGE_TEXT_COLOR}
				uppercase
				dataset={[
					['badgeType', processBadge.type],
					['dataNewsId', processBadge.newsId]
				]}
				classes={['news-badge']}
			/>
		</div>
	{/if}
	<p class="selectable-card__description">{description}</p>
</div>

<style lang="scss">
	@use '../../styles/modals';
	@import '../../styles/global';

	.selectable-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		padding: 20px;
		gap: 10px;
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		background: white;
		cursor: pointer;
		transition:
			border-color 0.2s ease,
			border-width 0.2s ease,
			background-color 0.2s ease;
		min-width: 0;
		height: 18.5rem;
		max-width: 15.375rem;
		box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);

		&:hover {
			border: 1px solid $primaryColor;
			outline: none;
		}

		&--highlighted {
			border: 2px solid $primaryColor;
			background-color: rgba(from $primaryColor r g b / 8%);
		}
	}

	.selectable-card__title {
		@include modals.roboto-font(normal, 500, 0.875rem, #000);
		margin: 0;
		order: -1;
	}

	.selectable-card__image-wrapper {
		width: 100%;
		height: 11.5rem;
		display: flex;
		justify-content: center;
	}

	.selectable-card__image {
		max-width: 100%;
		height: fit-content;
		max-height: 12.875rem;
		object-fit: contain;
	}

	.selectable-card__image-placeholder {
		width: 100%;
		height: 5rem;
		background: #f0f2f8;
		border-radius: 4px;
	}

	.selectable-card__description {
		@include modals.roboto-font(normal, 400, 0.875rem, #000);
		margin: 0;
		line-height: 1.4;
		text-align: center;
	}
</style>
